import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { ButtonLink } from '@/components/ButtonLink'
import { I18NAwareLocalizationProvider } from '@/components/I18NAwareLocalizationProvider'
import { useFeedbackContext } from '@/contexts/FeedbackContext'
import { assertUserIsAdminOrRedirect } from '@/features/admin/helper/assertUserIsAdminOrRedirect'
import { WorkingTimeModelChangedUsersDialog } from '@/features/admin/working-time-models/create-or-edit/components/WorkingTimeModelChangedUsersDialog'
import { WorkingTimeModelCreateOrEditPendingComponent } from '@/features/admin/working-time-models/create-or-edit/components/WorkingTimeModelCreateOrEditPendingComponent'
import { WorkingTimeModelForm } from '@/features/admin/working-time-models/create-or-edit/components/WorkingTimeModelForm'
import { workingTimeModelFormOptions } from '@/features/admin/working-time-models/create-or-edit/helper/workingTimeModelFormOptions'
import { useCreateOrUpdateWorkingTimeModel } from '@/features/admin/working-time-models/create-or-edit/mutations/useCreateOrUpdateWorkingTimeModel'
import { useWorkingTimeModelSchema } from '@/features/admin/working-time-models/create-or-edit/schemas/workingTimeModelSchema'
import { WorkingTimeModelDto } from '@/generated/Messages/Basic_pb'
import { useAppForm } from '@/hooks/useAppForm'
import {
    useWorkingTimeModel,
    workingTimeModelOptions,
} from '@/queries/useWorkingTimeModel'

const seleniumPrefix = 'edit-working-time-models'

declare module '@tanstack/react-router' {
    interface HistoryState {
        /**
         * Allows passing of the current wtm, to the wtm create form.
         */
        wtmDto?: WorkingTimeModelDto | undefined
    }
}

const RouteComponent: React.FC = () => {
    const { id } = Route.useParams()
    const { t } = useTranslation()

    const {
        data: { workingTimeModel },
        refetch,
    } = useWorkingTimeModel(id)

    const createOrUpdateWorkingTimeModel = useCreateOrUpdateWorkingTimeModel()
    const workingTimeModelSchema = useWorkingTimeModelSchema()
    const { addFeedback } = useFeedbackContext()
    const form = useAppForm({
        ...workingTimeModelFormOptions(workingTimeModel),

        validators: {
            onSubmit: workingTimeModelSchema,
        },

        onSubmitInvalid: () => {
            addFeedback?.({
                variant: 'snackbar',
                severity: 'warning',
                title: t('snackbar.failedValidation'),
            })
        },

        onSubmit: async ({ value, formApi }) => {
            await createOrUpdateWorkingTimeModel.mutateAsync(value)

            await refetch()

            formApi.reset()
        },
    })

    return (
        <>
            <title>{t('workingTimeModel.urlEdit')}</title>

            <form.AppForm>
                <I18NAwareLocalizationProvider>
                    <form.AdminForm
                        title={t('workingTimeModel.edit')}
                        backTo={'/admin/working-time-models'}
                        additionalButtons={
                            <form.Subscribe
                                selector={state => state.isSubmitting}
                            >
                                {isDisabled => (
                                    <ButtonLink
                                        to={'/admin/working-time-models/create'}
                                        state={{ wtmDto: workingTimeModel }}
                                        disabled={isDisabled}
                                        data-selenium={`${seleniumPrefix}-duplicate`}
                                    >
                                        {t('workingTimeModel.duplicate')}
                                    </ButtonLink>
                                )}
                            </form.Subscribe>
                        }
                    >
                        <form.Blocker
                            data-selenium={`${seleniumPrefix}-blocker`}
                        />

                        <WorkingTimeModelForm form={form} />
                    </form.AdminForm>
                </I18NAwareLocalizationProvider>
            </form.AppForm>

            <WorkingTimeModelChangedUsersDialog
                {...createOrUpdateWorkingTimeModel.data}
                onClickClose={() => createOrUpdateWorkingTimeModel.reset()}
            />
        </>
    )
}

const PendingComponent: React.FC = () => {
    const { t } = useTranslation()

    return (
        <>
            <title>{t('workingTimeModel.urlEdit')}</title>

            <WorkingTimeModelCreateOrEditPendingComponent
                title={t('workingTimeModel.edit')}
            />
        </>
    )
}

export const Route = createFileRoute(
    '/__base-layout/admin/working-time-models/$id/edit'
)({
    component: RouteComponent,
    pendingComponent: PendingComponent,
    beforeLoad: async ({
        context: { user, grpcClients, queryClient },
        params: { id },
    }) => {
        assertUserIsAdminOrRedirect(user)

        await Promise.all([
            queryClient.ensureQueryData(
                workingTimeModelOptions(grpcClients, id)
            ),
        ])
    },
})
